import React, {useState} from "react";
import {Link} from "react-router-dom";

import {EditOutlined, HistoryOutlined, InfoCircleOutlined, LinkOutlined} from "@ant-design/icons";
import {Col, Descriptions, List, Pagination, Row, Statistic, Tabs, Tooltip} from "antd";

import {createReportOverviewLink} from "../../helpers";
import {ROP_MIN_LABEL} from "../../properties";
import {MaterialInfo, ExtendedWhereUsedFactors, FieldsNaming} from "../../variants";
import {DataCard} from "../helpers/cards/Card";
import {MaterialBlock} from "../helpers/cards/MaterialBlock";
import {StandardTable} from "../helpers/TablePanel";

const {TabPane} = Tabs;

export function MaterialCard({material, itemInfo, canEdit, onEdit, onHistoryOpen, leadTime, exceptions, projectId, rankingApplied, historyCount}) {
	const [currentExceptionPage, setCurrentExceptionPage] = useState(1);
	const exceptionsPageSize = 10;

	const showValues = data => {
		if (Array.isArray(data)) {
			return data.join(", ");
		}

		return data;
	};

	const getLeadTimeColumns = () => [
		{
			dataIndex: "Purchasing Document",
			title: "PO #",
		},
		{
			dataIndex: "Scheduled Quantity",
			title: "Quantity",
		},
		{
			dataIndex: "Purchase Order Date",
			title: "Purchase Order Date",
		},
		{
			dataIndex: "Delivery Date",
			title: "Delivery Date",
			// render: date => date.split(" ")[0],
		},
		{
			dataIndex: "Lead Time in Days",
			title: "Planned Delivery Time",
		},
		{
			dataIndex: "Actual Lead Time",
			title: "Actual Delivery Time",
		},
		{
			dataIndex: "Actual Lead Time",
			title: "Diff in days",
			render: (lt_actual, item) => Math.abs(lt_actual - item["Lead Time in Days"]),
		},
		{
			dataIndex: "Actual Lead Time",
			title: "Diff in %",
			render: (lt_actual, item) =>
				Math.round((Math.abs(lt_actual - item["Lead Time in Days"]) / item["Lead Time in Days"]) * 100),
		},
	];

	const paginatedExceptions = exceptions?.slice(
		(currentExceptionPage - 1) * exceptionsPageSize,
		currentExceptionPage * exceptionsPageSize,
	);

	return (
		<DataCard
			title={
				<Row>
					<Col span={20}>
						<Row>
							<div style={{marginRight: 5}}>Material:</div>
							<MaterialBlock
								material={material}
								children={
									<div>
										{material} - {itemInfo.description} <LinkOutlined />
									</div>
								}
							/>
							{canEdit && !itemInfo.approved && (
								<EditOutlined
									style={{marginLeft: 5, marginTop: 5}}
									onClick={() => onEdit({editModal: true})}
								/>
							)}
						</Row>
					</Col>
					<Col span={4}>
						<span
							className="url-link float-right"
							style={{marginLeft: 10}}
							onClick={() => onHistoryOpen({historyModal: true})}>
							History {historyCount != null ? `(${historyCount})` : ""} <HistoryOutlined />
						</span>
					</Col>
				</Row>
			}>
			{itemInfo.approved && (
				<div style={{color: "green"}}>The {ROP_MIN_LABEL}/MAXES were overridden manually!</div>
			)}
			{itemInfo.review_date && (
				<Descriptions
					bordered
					size={"small"}
					column={itemInfo.is_reviewed && itemInfo.review_expiry_date ? 2 : 1}
					style={{marginBottom: 10}}
					labelStyle={{fontWeight: "bold", backgroundColor: "#e6f4ff"}}
					contentStyle={{backgroundColor: "#e6f4ff"}}>
					<Descriptions.Item
						label={
							<span>
								Last DIGATEX Review Date{" "}
								<Tooltip
									title={
										<div>
											<div>Reviewed by: {itemInfo.reviewed_by || "NA"}</div>
											{itemInfo.review_notes && <div>Notes: {itemInfo.review_notes}</div>}
										</div>
									}>
									<InfoCircleOutlined />
								</Tooltip>
							</span>
						}>
						{itemInfo.review_date?.split("T")[0]}
					</Descriptions.Item>
					{itemInfo.is_reviewed && itemInfo.review_expiry_date && (
						<Descriptions.Item label="Review Expiry Date">
							{itemInfo.review_expiry_date?.split("T")[0]}
						</Descriptions.Item>
					)}
				</Descriptions>
			)}
			{itemInfo["valid_for_optimisation"] === 0 && (
				<div style={{color: "red"}}>
					Material {material} is not optimised by default due to project configuration, but could be included
					in the scenarios
				</div>
			)}
			{rankingApplied === "stocking_calculator" && (!itemInfo.equipment_info || itemInfo.equipment_info.length === 0) && (
                <div style={{ color: "red", marginTop: 5 }}>
                    The material is not attached to any flocs, the recommended min/maxes are 0, please select another option for ranking
				</div>
			)}
			<Tabs defaultActiveKey={"1"} style={{marginTop: -15}}>
				<TabPane tab={"Material Data"} key={"1"}>
					<div>{MaterialInfo.MainAttributes(itemInfo, showValues)}</div>
				</TabPane>
				{itemInfo.plant_info && (
					<TabPane tab={`SKU's Data (${itemInfo.plant_info.length || 0})`} key={"2"}>
						<StandardTable
							tableSize={"small"}
							data={itemInfo.plant_info}
							columns={MaterialInfo.SKUTableColumns()}
						/>
					</TabPane>
				)}
				{!ExtendedWhereUsedFactors.enabled && (<TabPane tab={"Where-Used factors"} key={"3"}>
					<Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
						<Descriptions.Item label="Installed per Unit (Max)">{itemInfo.max_installed}</Descriptions.Item>
						<Descriptions.Item span={2} label={`Highest ${MaterialInfo.eqCrit} Criticality`}>
							{itemInfo.criticality}
						</Descriptions.Item>
						<Descriptions.Item span={1} label="Count of FLOCs">
							{itemInfo.equipment || 0}
						</Descriptions.Item>
						<Descriptions.Item label="Total Quantity Installed">
							{itemInfo.total_installed}
						</Descriptions.Item>
					</Descriptions>
				</TabPane>)
				}
				{MaterialInfo.showLeadTimeVariation && (
					<TabPane tab={`Lead Time Variation (${leadTime.length})`} key={"4"}>
						<StandardTable tableSize={"small"} data={leadTime} columns={getLeadTimeColumns()} />
					</TabPane>
				)}
				<TabPane tab={`Exceptions (${exceptions?.length || 0})`} key={"5"}>
					Exceptions:
					{exceptions && exceptions.length > 0 ? (
						<>
							<ul style={{paddingLeft: 20, listStyle: "none", margin: 0}}>
								{paginatedExceptions.map((exception, index) => (
									<li
										key={`${exception.report_id}-${index}`}
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											padding: "4px 0",
											borderBottom: "1px solid #f0f0f0",
										}}>
										<span>
											{exception.message.charAt(0).toUpperCase() + exception.message.slice(1)}
										</span>
										<Link
											className={"beautiful-link"}
											to={createReportOverviewLink(projectId, exception.report_id)}>
											Open report
										</Link>
									</li>
								))}
							</ul>
							{exceptions.length > exceptionsPageSize && (
								<Pagination
									style={{marginTop: 16, textAlign: "right"}}
									current={currentExceptionPage}
									pageSize={exceptionsPageSize}
									total={exceptions.length}
									onChange={setCurrentExceptionPage}
									simple
									size="small"
								/>
							)}
						</>
					) : (
						<p style={{marginTop: 3}}>No exceptions reported for this material.</p>
					)}
				</TabPane>
				{itemInfo.demand_info && (
					<TabPane tab={"Demand Analysis"} key={"demand_analysis"}>
						<Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
							<Descriptions.Item span={2} label="Demand Quantity Variability">
								{itemInfo.demand_info.qty_cov}
							</Descriptions.Item>
							<Descriptions.Item label="Demand Time Variability">
								{itemInfo.demand_info.time_cov}
							</Descriptions.Item>
							<Descriptions.Item span={3} label="Forecastability Type">
								{itemInfo.demand_info.forecastability_type}
							</Descriptions.Item>
						</Descriptions>
					</TabPane>
				)}
				{itemInfo.po_info && (
					<TabPane tab={"Supply Analysis"} key={"po_analysis"}>
						<Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
							<Descriptions.Item span={2} label="Total PO">
								{itemInfo.po_info.total_pos}
							</Descriptions.Item>
							<Descriptions.Item label={FieldsNaming.planned_lead_time}>
								{itemInfo.po_info.planned_lead_time}
							</Descriptions.Item>
							<Descriptions.Item span={3} label={FieldsNaming.po_to_rcpt}>
								<Row>
									<Col span={8}>
										<Statistic
											title={"Mean"}
											value={itemInfo.po_info.po_to_rcpt_analysis.mean || "NA"}
											valueStyle={{fontSize: 14}}
										/>
									</Col>
									<Col span={8}>
										<Statistic
											title={"Std Deviation"}
											value={itemInfo.po_info.po_to_rcpt_analysis.std_dev}
											valueStyle={{fontSize: 14}}
										/>
									</Col>
									<Col span={8}>
										<Statistic
											title={"CoV"}
											value={itemInfo.po_info.po_to_rcpt_analysis.cov}
											valueStyle={{fontSize: 14}}
										/>
									</Col>
								</Row>
							</Descriptions.Item>
							{FieldsNaming.show_pr_to_po && (<Descriptions.Item span={3} label="PR to PO">
								<Row>
									<Col span={8}>
										<Statistic
											title={"Mean"}
											value={itemInfo.po_info.pr_to_po_analysis.mean}
											valueStyle={{fontSize: 14}}
										/>
									</Col>
									<Col span={8}>
										<Statistic
											title={"Std Deviation"}
											value={itemInfo.po_info.pr_to_po_analysis.std_dev}
											valueStyle={{fontSize: 14}}
										/>
									</Col>
									<Col span={8}>
										<Statistic
											title={"CoV"}
											value={itemInfo.po_info.pr_to_po_analysis.cov}
											valueStyle={{fontSize: 14}}
										/>
									</Col>
								</Row>
							</Descriptions.Item>)}
						</Descriptions>
					</TabPane>
				)}
				{itemInfo.pm_info && (
					<TabPane tab={"Maintenance Analysis"} key={"maintenance_analysis"}>
						<Descriptions labelStyle={{fontWeight: "bold"}} bordered size={"small"}>
							<Descriptions.Item span={2} label="Planned WO Executed">
								{itemInfo.pm_info.plWO}
							</Descriptions.Item>
							<Descriptions.Item label="Corrective WO Executed">{itemInfo.pm_info.cWO}</Descriptions.Item>
							<Descriptions.Item span={3} label="PM to CM Ratio">
								{itemInfo.pm_info.pm_cm_ratio}
							</Descriptions.Item>
						</Descriptions>
					</TabPane>
				)}
				{MaterialInfo.showFollowOn && (
					<TabPane tab={`Follow-On`} key={"followOn"}>
						{itemInfo.previous?.length > 0 && (
							<div style={{margin: 5}}>
								<List
									size="small"
									dataSource={itemInfo.previous}
									renderItem={item => (
										<List.Item>
											<MaterialBlock material={item}>{item}</MaterialBlock>
										</List.Item>
									)}
								/>
							</div>
						)}
						{itemInfo.follow_on?.length > 0 && (
							<div style={{margin: 5}}>
								<List
									size="small"
									dataSource={itemInfo.follow_on}
									renderItem={item => (
										<List.Item>
											<MaterialBlock material={item}>{item}</MaterialBlock>
										</List.Item>
									)}
								/>
							</div>
						)}
					</TabPane>
				)}
			</Tabs>
		</DataCard>
	);
}
